'use client';

import { useCallback, useState } from 'react';

import { useParams } from 'next/navigation';

import { ChevronDown, ChevronUp, Shield } from 'lucide-react';

import { useCookieConsent } from './cookie-consent-context';

const translations = {
  de: {
    description:
      'Wir verwenden Cookies. Analyse- und Marketing-Cookies nur mit Zustimmung.',
    customize: 'Anpassen',
    essentialLabel: 'Essentiell',
    essentialBadge: 'Immer aktiv',
    analyticsLabel: 'Analyse',
    marketingLabel: 'Marketing',
    saveSelection: 'Speichern',
    rejectAll: 'Ablehnen',
    acceptAll: 'Akzeptieren',
    privacyLink: 'Datenschutz',
    privacyHref: '/datenschutz',
  },
  en: {
    description:
      'We use cookies. Analytics and marketing cookies only with your consent.',
    customize: 'Customize',
    essentialLabel: 'Essential',
    essentialBadge: 'Always on',
    analyticsLabel: 'Analytics',
    marketingLabel: 'Marketing',
    saveSelection: 'Save',
    rejectAll: 'Reject all',
    acceptAll: 'Accept all',
    privacyLink: 'Privacy',
    privacyHref: '/privacy-policy',
  },
};

/**
 * Compact cookie consent banner compliant with FDPIC October 2025 guidelines.
 *
 * Exemption for jurisdictions without cookie consent requirements (e.g. POPIA)
 * is handled upstream by CookieConsentExemption, which suppresses showBanner
 * via the context. This component only checks showBanner.
 *
 * Requirements met:
 * - Equal prominence for Accept and Reject buttons (no dark patterns)
 * - Granular category selection (expandable)
 * - Pre-ticked boxes prohibited: all non-essential categories unchecked by default
 * - No tracking scripts load until consent is given
 */
export function CookieConsentBanner() {
  const { showBanner, acceptAll, rejectAll, acceptSelected } =
    useCookieConsent();
  const [expanded, setExpanded] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(false);
  const [marketingChecked, setMarketingChecked] = useState(false);

  const params = useParams();
  const locale = typeof params?.locale === 'string' ? params.locale : 'en';
  const t = locale === 'de' ? translations.de : translations.en;

  const handleSaveSelection = useCallback(() => {
    acceptSelected({
      analytics: analyticsChecked,
      marketing: marketingChecked,
    });
  }, [acceptSelected, analyticsChecked, marketingChecked]);

  if (!showBanner) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[90] px-4 pb-4">
      <div className="bg-card/95 mx-auto max-w-xl rounded-lg border shadow-md backdrop-blur-sm">
        <div className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:gap-4 sm:p-3">
          {/* Text */}
          <p className="text-muted-foreground min-w-0 flex-1 text-xs leading-snug">
            {t.description}{' '}
            <a
              href={t.privacyHref}
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5 underline underline-offset-2"
            >
              <Shield className="inline h-2.5 w-2.5" />
              {t.privacyLink}
            </a>
          </p>

          {/* Actions */}
          <div className="flex shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="text-muted-foreground hover:text-foreground flex items-center gap-0.5 text-xs transition-colors"
            >
              {t.customize}
              {expanded ? (
                <ChevronUp className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </button>
            <button
              type="button"
              onClick={rejectAll}
              className="border-border text-foreground hover:bg-muted rounded-md border px-3 py-1.5 text-xs font-medium transition-colors"
            >
              {t.rejectAll}
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="border-border text-foreground hover:bg-muted rounded-md border px-3 py-1.5 text-xs font-medium transition-colors"
            >
              {t.acceptAll}
            </button>
          </div>
        </div>

        {/* Expandable category selection */}
        {expanded && (
          <div className="border-t px-3 pt-2 pb-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
              <label className="text-muted-foreground flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={true}
                  disabled={true}
                  className="h-3.5 w-3.5 rounded"
                />
                {t.essentialLabel}
                <span className="text-muted-foreground/60 text-[10px]">
                  ({t.essentialBadge})
                </span>
              </label>
              <label className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={analyticsChecked}
                  onChange={(e) => setAnalyticsChecked(e.target.checked)}
                  className="h-3.5 w-3.5 rounded"
                />
                {t.analyticsLabel}
              </label>
              <label className="flex cursor-pointer items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={marketingChecked}
                  onChange={(e) => setMarketingChecked(e.target.checked)}
                  className="h-3.5 w-3.5 rounded"
                />
                {t.marketingLabel}
              </label>
              <button
                type="button"
                onClick={handleSaveSelection}
                className="text-primary hover:text-primary/80 text-xs font-medium underline underline-offset-2 transition-colors"
              >
                {t.saveSelection}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
